/**
 * The browser's connection to the registry's live stream.
 *
 * One stream per page carries everything the platform pushes: `flags` events
 * replace the page's flags, and any other event (`resolved`, when the caller's
 * plugins change) is handed to whoever listens for it. The stream ends itself
 * every few minutes so a fresh token is used; it is reopened at once. When it
 * cannot be reached it is retried with backoff, and meanwhile the flags are
 * fetched on an interval, so a page never stops hearing about them - only
 * hears later. `liveState()` says which of those is happening, so a host with
 * its own poll can run it only while the stream is down.
 */
import { isJsonObject, type JsonValue, parseJson } from '@crown/framework/ir';
import { Signal } from '@crown/signal';
import { onlyBooleans, seedFlags } from './store';

export type LiveState = 'idle' | 'connecting' | 'open' | 'down';

export interface LiveSource {
  /** `GET` answers `{ flags }` for the caller. */
  readonly flagsUrl: string;
  /** The server-sent event stream. */
  readonly liveUrl: string;
  /**
   * `GET` answers the caller's resolved plugins; asked while the stream is
   * down and handed to `resolved` listeners, so a kill still arrives - later.
   */
  readonly resolvedUrl?: string;
  /** The caller's credentials, asked for on every connection so a refreshed token is used. */
  headers(): Promise<Readonly<Record<string, string>>> | Readonly<Record<string, string>>;
}

/**
 * The plugin registry as a live source: its `/api/flags` and `/api/live`.
 * With `hostApiVersion`, the stream also carries `resolved` events - the
 * plugins, first-party MFEs among them, the platform gives the caller.
 */
export function registrySource(
  registryUrl: string,
  headers: LiveSource['headers'],
  options: { readonly hostApiVersion?: number } = {},
): LiveSource {
  const base = registryUrl.replace(/\/+$/, '');
  if (options.hostApiVersion === undefined) return { flagsUrl: `${base}/api/flags`, liveUrl: `${base}/api/live`, headers };
  const query = `?hostApiVersion=${options.hostApiVersion}`;
  return {
    flagsUrl: `${base}/api/flags`,
    liveUrl: `${base}/api/live${query}`,
    resolvedUrl: `${base}/api/plugins/resolve/context${query}`,
    headers,
  };
}

export interface LiveTiming {
  /** How often the flags are fetched while the stream is down. */
  readonly pollMs: number;
  /** Waits before reconnecting a stream that failed, one after another; the last repeats. */
  readonly reconnectMs: readonly number[];
}

export const LIVE_TIMING: LiveTiming = { pollMs: 60_000, reconnectMs: [1_000, 5_000, 15_000, 60_000, 300_000] };

const STATE = Symbol.for('crown.live.state');
const LISTENERS = Symbol.for('crown.live.listeners');
const holder: typeof globalThis & {
  [STATE]?: Signal.State<LiveState>;
  [LISTENERS]?: Map<string, Set<(data: JsonValue) => void>>;
} = globalThis;

function stateSignal(): Signal.State<LiveState> {
  holder[STATE] ??= new Signal.State<LiveState>('idle');
  return holder[STATE];
}

function listeners(): Map<string, Set<(data: JsonValue) => void>> {
  holder[LISTENERS] ??= new Map();
  return holder[LISTENERS];
}

/** Whether the page is following the stream; a tracked read. */
export function liveState(): LiveState {
  return stateSignal().get();
}

/** Calls `listener` with each `name` event's data; returns what stops it. */
export function onLiveEvent(name: string, listener: (data: JsonValue) => void): () => void {
  const map = listeners();
  let set = map.get(name);
  if (!set) {
    set = new Set();
    map.set(name, set);
  }
  set.add(listener);
  return () => set.delete(listener);
}

function dispatch(name: string, raw: string): void {
  const parsed = parseJson(raw);
  if (!parsed.ok) return;
  const data = parsed.value;
  if (name === 'flags') seedFlags(onlyBooleans(isJsonObject(data) ? data.flags : undefined));
  for (const listener of listeners().get(name) ?? []) {
    try {
      listener(data);
    } catch (error) {
      console.error(`[@crown/flags] a "${name}" listener threw`, error);
    }
  }
}

function readEvent(block: string): { name: string; data: string } | null {
  let name = 'message';
  const data: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
    if (field === 'event') name = value;
    else if (field === 'data') data.push(value);
  }
  return data.length > 0 ? { name, data: data.join('\n') } : null;
}

/**
 * Follows the live stream until the returned function is called. A second call
 * while one is running returns the running one's stop.
 */
let running: (() => void) | null = null;

export function startLive(source: LiveSource, timing: LiveTiming = LIVE_TIMING): () => void {
  if (running) return running;
  const state = stateSignal();
  let stopped = false;
  let failures = 0;
  let controller: AbortController | null = null;
  let reconnect: ReturnType<typeof setTimeout> | null = null;
  let poll: ReturnType<typeof setInterval> | null = null;

  const fetchJson = async (url: string): Promise<JsonValue | undefined> => {
    try {
      const response = await fetch(url, { headers: { ...(await source.headers()) }, cache: 'no-store' });
      if (!response.ok) return undefined;
      const parsed = parseJson(await response.text());
      return parsed.ok ? parsed.value : undefined;
    } catch {
      // The next poll or connection tries again.
      return undefined;
    }
  };
  /** What the stream would have said, asked for directly while it cannot say it. */
  const fetchFlags = async (): Promise<void> => {
    const [flagsBody, resolvedBody] = await Promise.all([
      fetchJson(source.flagsUrl),
      source.resolvedUrl ? fetchJson(source.resolvedUrl) : Promise.resolve(undefined),
    ]);
    if (stopped) return;
    if (flagsBody !== undefined) seedFlags(onlyBooleans(isJsonObject(flagsBody) ? flagsBody.flags : undefined));
    if (resolvedBody !== undefined) dispatch('resolved', JSON.stringify(resolvedBody));
  };

  const startPolling = () => {
    if (poll || stopped) return;
    void fetchFlags();
    poll = setInterval(() => void fetchFlags(), timing.pollMs);
  };
  const stopPolling = () => {
    if (poll) clearInterval(poll);
    poll = null;
  };

  const connect = async (): Promise<void> => {
    reconnect = null;
    if (stopped) return;
    controller = new AbortController();
    if (state.get() !== 'down') state.set('connecting');
    let received = false;
    try {
      const response = await fetch(source.liveUrl, {
        headers: { Accept: 'text/event-stream', ...(await source.headers()) },
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok || !response.body) throw new Error(`the live stream answered ${response.status}`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n?/g, '\n');
        for (let end = buffer.indexOf('\n\n'); end !== -1; end = buffer.indexOf('\n\n')) {
          const event = readEvent(buffer.slice(0, end));
          buffer = buffer.slice(end + 2);
          // `hello` only says the server accepted the connection; the stream is open
          // when it delivers what it is for.
          if (!event || event.name === 'hello') continue;
          if (!received) {
            received = true;
            failures = 0;
            stopPolling();
            state.set('open');
          }
          dispatch(event.name, event.data);
        }
      }
    } catch {
      // Falls through to reconnecting.
    }
    controller = null;
    if (stopped) return;
    if (received) {
      // A stream that delivered and then ended was closed on purpose: reopen it at once.
      state.set('connecting');
      reconnect = setTimeout(() => void connect(), 0);
      return;
    }
    state.set('down');
    startPolling();
    const wait = timing.reconnectMs[Math.min(failures, timing.reconnectMs.length - 1)] ?? 60_000;
    failures++;
    reconnect = setTimeout(() => void connect(), wait);
  };

  void connect();

  const stop = () => {
    stopped = true;
    running = null;
    controller?.abort();
    if (reconnect) clearTimeout(reconnect);
    stopPolling();
    state.set('idle');
  };
  running = stop;
  return stop;
}
